import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { sentryCaptureException } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    sentryCaptureException(error, { componentStack: errorInfo.componentStack });
  }

  handleReset = () => {
    // Limpar portais Radix que podem estar "órfãos" causando conflito de DOM
    try {
      document.querySelectorAll('[data-radix-portal]').forEach(el => {
        try { el.remove(); } catch (e) { /* ignore */ }
      });
      console.log("[ErrorBoundary] Portais órfãos limpos");
    } catch (e) {
      console.error("[ErrorBoundary] Erro ao limpar portais:", e);
    }
    
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full p-6 text-center space-y-4 animate-fade-in">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">
              Algo deu errado
            </h2>
            <p className="text-muted-foreground">
              {this.state.error?.message || "Ocorreu um erro inesperado."}
            </p>
            <Button
              onClick={this.handleReset}
              className="w-full"
            >
              Recarregar Página
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
